import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
// import logo from "@/assets/images/logo_aip.png";
// import logoWhite from "@/assets/images/logo-white.png";


import logoWhite from "@/assets/images/logo-white.png";
import logoDark from "@/assets/images/logo-dark.png";

const getNavLinks = (t: (key: string) => string) => [
  { label: t("navHome"), path: "/" },
  { label: t("navAbout"), path: "/about" },
  { label: t("navServices"), path: "/services" },
  { label: t("navBusiness"), path: "/business" },
  { label: t("navTenders"), path: "/tenders" },
  { label: t("navEvents"), path: "/events" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const langRef = useRef<HTMLDivElement>(null);

  const navLinks = getNavLinks(t);

  useEffect(() => {
    const NAV_Y = 64; // vertical midpoint of navbar

    const updateNavState = () => {
      const darkSections = document.querySelectorAll("[data-dark-section]");
      let overDark = false;
      darkSections.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top <= NAV_Y && rect.bottom > NAV_Y) {
          overDark = true;
        }
      });
      setScrolled(!overDark);
    };

    const timer = setTimeout(() => {
      updateNavState();
      window.addEventListener("scroll", updateNavState, { passive: true });
    }, 80);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", updateNavState);
    };
  }, [location.pathname]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setLangOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-white/98 backdrop-blur-xl shadow-sm py-2 px-4"
            : "bg-transparent py-4 px-4"
        }`}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            {/* <Link to="/" className="flex-shrink-0 flex items-center relative z-10">
              <img
                alt="Africa Investment Portal"
                className="h-20 w-auto object-contain transition-all duration-300"
                src={logo}
              />
            </Link> */}

            <Link
  to="/"
  className="flex items-center relative z-10 w-64"
>

  <img
  src={scrolled ? logoDark : logoWhite}
  alt="Africa Investment Portal"
  className="h-24 w-auto object-contain transition-all duration-300"
/>


</Link>

            {/* Desktop Nav Links */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`whitespace-nowrap cursor-pointer px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? scrolled
                          ? "text-[#00C2C2] bg-[#E6FAFA]"
                          : "text-white bg-white/20"
                        : scrolled
                          ? "text-gray-800 hover:text-[#00C2C2] hover:bg-[#E6FAFA]"
                          : "text-white/90 hover:text-white hover:bg-white/15"
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>

            {/* Desktop Right: Language + CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <div className="relative" ref={langRef}>
                <button
                  onClick={() => setLangOpen(!langOpen)}
                  className={`whitespace-nowrap cursor-pointer flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    scrolled
                      ? "text-gray-800 hover:bg-[#E6FAFA] hover:text-[#00C2C2]"
                      : "text-white/90 hover:text-white hover:bg-white/15"
                  }`}
                >
                  <i className="ri-global-line text-base" />
                  {i18n.language === "fr" ? "FR" : "EN"}
                  <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? "rotate-180" : ""}`} />
                </button>
                {langOpen && (
                  <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-gray-100 py-1 min-w-[120px] z-50">
                    <button
                      onClick={() => changeLanguage("en")}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-[#E6FAFA] transition-colors cursor-pointer ${
                        i18n.language === "en" ? "text-[#00C2C2] font-semibold" : "text-gray-600"
                      }`}
                    >
                      English
                    </button>
                    <button
                      onClick={() => changeLanguage("fr")}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-[#E6FAFA] transition-colors cursor-pointer ${
                        i18n.language === "fr" ? "text-[#00C2C2] font-semibold" : "text-gray-600"
                      }`}
                    >
                      Français
                    </button>
                  </div>
                )}
              </div>
              <Link
                to="/contact"
                className={`whitespace-nowrap cursor-pointer px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                  scrolled
                    ? "bg-[#00C2C2] text-white hover:bg-[#00AAAA]"
                    : "bg-white text-[#00C2C2] hover:bg-white/90"
                }`}
              >
                {t("navContact")}
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full bg-[#00C2C2] text-white transition-colors cursor-pointer hover:bg-[#00AAAA] shadow-sm"
              aria-label="Toggle menu"
            >
              <i className="text-xl ri-menu-3-line" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[60] transition-all duration-300 lg:hidden ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/50" onClick={() => setMenuOpen(false)} />
        <div
          className={`absolute top-0 right-0 h-full w-72 bg-white flex flex-col transition-transform duration-300 ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
            <Link to="/" onClick={() => setMenuOpen(false)}>
              <img
                alt="AIP"
                className="h-24 w-auto object-contain"
                src={logoDark}
              />
            </Link>
            <button
              onClick={() => setMenuOpen(false)}
              className="w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 cursor-pointer"
              aria-label="Close menu"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center px-4 py-3 rounded-xl font-medium transition-colors cursor-pointer ${
                  location.pathname === link.path
                    ? "text-[#00C2C2] bg-[#E6FAFA]"
                    : "text-gray-800 hover:bg-[#E6FAFA] hover:text-[#00C2C2]"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
          <div className="px-4 pb-8 pt-4 space-y-3 border-t border-gray-100">
            <div className="flex gap-2">
              <button
                onClick={() => { changeLanguage("en"); setMenuOpen(false); }}
                className={`flex-1 py-2 rounded-xl text-sm font-medium cursor-pointer ${
                  i18n.language === "en"
                    ? "bg-[#E6FAFA] text-[#00C2C2]"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                EN
              </button>
              <button
                onClick={() => { changeLanguage("fr"); setMenuOpen(false); }}
                className={`flex-1 py-2 rounded-xl text-sm font-medium cursor-pointer ${
                  i18n.language === "fr"
                    ? "bg-[#E6FAFA] text-[#00C2C2]"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                FR
              </button>
            </div>
            <Link
              to="/contact"
              onClick={() => setMenuOpen(false)}
              className="whitespace-nowrap cursor-pointer block text-center px-4 py-3 rounded-xl bg-[#00C2C2] text-white font-semibold hover:bg-[#00AAAA] transition-colors"
            >
              {t("navContact")}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}